import { Client } from "@/Common/Api/Client";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { Person } from "@/Common/Models/Person/Person";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { C1LabelDto } from "../Api/Labels/Dto/C1LabelDto";
import { GenericLabelDto } from "../Api/Labels/Dto/GenericLabelDto";
import { RLabelDto } from "../Api/Labels/Dto/RLabelDto";
import { TextLabelDto } from "../Api/Labels/Dto/TextLabelDto";

class LabelHelper {
    /**
     * Create the C1 label
     * @param {Person} person
     * @param {string} familyDoctorName
     */
    public static async generateC1Label(person: Person, familyDoctorName: string = ""): Promise<void> {
        const isPrintingPortrait = !settingsStore.configuration.get("ArePrintLabelsPortrait");
        const isPrintingLargeLabels = settingsStore.configuration.get("IsLargePrint");

        const labelDto: C1LabelDto = {
            PatientContactId: person.ContactId,
            PatientContactFacetId: person.ContactFacetId,
            IsC1: true,
            FamilyDoctorName: familyDoctorName,
            CustomHeightInches: null,
            CustomWidthInches: null,
            IsPortraitPrinting: isPrintingPortrait
        };

        if (isPrintingLargeLabels) {
            labelDto.CustomWidthInches = 3.5;
            labelDto.CustomHeightInches = 1.1417;
        }

        await LabelHelper.generateLabelAndOpenPreview(labelDto);
    }

    /**
     * Create the C2 label
     * @param {Person} person
     * @param {string} familyDoctorName
     */
    public static async generateC2Label(person: Person, familyDoctorName: string = ""): Promise<void> {
        const labelDto: GenericLabelDto = {
            PatientContactId: person.ContactId,
            PatientContactFacetId: person.ContactFacetId,
            IsC2: true,
            FamilyDoctorName: familyDoctorName
        };

        await LabelHelper.generateLabelAndOpenPreview(labelDto);
    }

    /**
     * Create the C3 label
     * @param {Person} person
     * @param {string} familyDoctorName
     */
    public static async generateC3Label(person: Person, familyDoctorName: string = ""): Promise<void> {
        const labelDto: GenericLabelDto = {
            PatientContactId: person.ContactId,
            PatientContactFacetId: person.ContactFacetId,
            IsC3: true,
            FamilyDoctorName: familyDoctorName
        };

        await LabelHelper.generateLabelAndOpenPreview(labelDto);
    }

    /**
     * Create the R label
     * @param {Person} person
     * @param {string} clinicianContactId
     */
    public static async generateRLabel(person: Person, clinicianContactId: string): Promise<void> {
        let clinicianName = "";
        let clinicName = "";
        let clinicAddress = "";
        let clinicPhone = "";

        const clinician = cacheStore.GetCliniciansPersonsByContactId(clinicianContactId);
        if (clinician != null) {
            clinicianName = clinician.NameWithPrefix;
            if (clinician.EmployeeModel && clinician.EmployeeModel.Licences && clinician.EmployeeModel.Licences.length > 0)
                clinicianName = `${clinicianName} (${clinician.EmployeeModel.Licences[0].LicenceNumber})`;

            if (clinician.EmployeeModel && clinician.EmployeeModel.ActiveSiteId && clinician.EmployeeModel.ActiveSiteId.length > 0) {
                const site = cacheStore.GetSiteById(clinician.EmployeeModel.ActiveSiteId);

                if (site) {
                    clinicName = site.Model.ClinicalName;
                    clinicAddress = site.getAddressLabel();
                    clinicPhone = site.getPhoneLabel();
                }
            }
        }

        const labelDto: RLabelDto = {
            PatientContactId: person.ContactId,
            PatientContactFacetId: person.ContactFacetId,
            IsR: true,
            EmployeeNameString: clinicianName,
            ClinicNameString: clinicName,
            ClinicAddressString: clinicAddress,
            ClinicPhoneNumberString: clinicPhone
        };

        await LabelHelper.generateLabelAndOpenPreview(labelDto);
    }

    /**
     * Create the A label
     * @param {Person} person
     */
    public static async generateALabel(person: Person): Promise<void> {
        const labelDto: GenericLabelDto = {
            PatientContactId: person.ContactId,
            PatientContactFacetId: person.ContactFacetId,
            IsA: true
        };

        await LabelHelper.generateLabelAndOpenPreview(labelDto);
    }

    /**
     * Create the billing label
     * @param {Person} person
     * @param {string} familyDoctorName
     */
    public static async generateBillingLabel(person: Person, familyDoctorName: string = ""): Promise<void> {
        const labelDto: GenericLabelDto = {
            PatientContactId: person.ContactId,
            PatientContactFacetId: person.ContactFacetId,
            IsBilling: true,
            FamilyDoctorName: familyDoctorName
        };

        await LabelHelper.generateLabelAndOpenPreview(labelDto);
    }

    /**
     * Create the text label
     * @param {Person} person
     * @param {string} freeText
     */
    public static async generateTextLabel(person: Person, freeText: string): Promise<void> {
        const labelDto: TextLabelDto = {
            PatientContactId: person.ContactId,
            PatientContactFacetId: person.ContactFacetId,
            IsTx: true,
            FreeText: freeText
        };

        await LabelHelper.generateLabelAndOpenPreview(labelDto);
    }

    /**
     * Create the label and open the preview
     * @param {GenericLabelDto | C1LabelDto | TextLabelDto | RLabelDto} labelDto
     */
    private static async generateLabelAndOpenPreview(labelDto: GenericLabelDto | C1LabelDto | TextLabelDto | RLabelDto): Promise<void> {
        const response = await Client.postAsync<{ FileName: string }>(
            ServerOperations.Server.Myle_Patient_GenerateLabel,
            { labelDTOs: [labelDto] }
        );

        if (response.isSuccess) {
            const fileName = response.data.FileName;
            PrintHelper.ShowPrintPreviewFileName(fileName);
        }
    }
}

export { LabelHelper };
